/**
 * SiteAffiliatesTab — per-website affiliate configuration.
 *
 * Left nav: network selector (EPN, Amazon, AWIN, CJ).
 * Right panel: network-specific config + advertiser/product selection.
 *
 * Account-level API credentials live in Settings > Affiliates.
 * This tab handles per-site overrides: campaign IDs, tracking IDs,
 * active advertiser lists, product feeds, and keyword search terms.
 */
import { useState, useEffect, useRef, FormEvent } from "react";
import {
  Building2,
  Save,
  CheckCircle2,
  Search,
  RefreshCw,
  Plus,
  X,
  Star,
  ExternalLink,
  Package,
  AlertCircle,
  Info,
  ChevronRight,
  Rss,
  Trash2,
} from "lucide-react";
import { affiliates, sites } from "../lib/api";
import type {
  Site,
  AffiliateConfig,
  SiteAffiliateConfig,
  SiteAwinConfig,
  SiteCjConfig,
  AwinProgram,
  CjAdvertiser,
  SiteProductFeed,
  LiveProduct,
} from "../lib/api";

// ─── Types ───────────────────────────────────────────────────

type NetworkId = "ebay" | "amazon" | "awin" | "cj";

const NETWORKS: { id: NetworkId; label: string; abbr: string; color: string }[] = [
  { id: "ebay", label: "eBay Partner Network", abbr: "eB", color: "amber" },
  { id: "amazon", label: "Amazon Associates", abbr: "Az", color: "orange" },
  { id: "awin", label: "AWIN", abbr: "Aw", color: "blue" },
  { id: "cj", label: "Commission Junction", abbr: "CJ", color: "green" },
];

const AMAZON_REGIONS = ["us-east-1", "eu-west-1", "us-west-2", "ap-northeast-1"] as const;
const AMAZON_REGION_LABELS: Record<string, string> = {
  "us-east-1": "US",
  "eu-west-1": "EU",
  "us-west-2": "CA",
  "ap-northeast-1": "JP",
};

// ─── Small helpers ───────────────────────────────────────────

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="pb-3 border-b border-gray-800 mb-4">
      <p className="text-sm font-semibold text-gray-100">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500";
const labelCls = "block text-xs font-medium text-gray-400 mb-1.5";
const selectCls = inputCls;

function InfoBox({ text }: { text: string }) {
  return (
    <div className="flex gap-2 px-3 py-2.5 bg-blue-500/5 border border-blue-500/15 rounded-lg">
      <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-blue-200/70">{text}</p>
    </div>
  );
}

function NotConnectedBanner({ network }: { network: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
      <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
      <p className="text-xs text-amber-300">
        {network} credentials not connected.{" "}
        <span className="text-amber-400 font-medium">Go to Settings → Affiliates to add them.</span>
      </p>
    </div>
  );
}

// ─── Keyword / search-term chip list ─────────────────────────

function KeywordChips({
  label,
  prefix,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  prefix: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [input, setInput] = useState("");

  function addKeyword() {
    const kw = input.trim().replace(/\s+/g, "+");
    if (!kw) return;
    const entry = `${prefix}:search:${kw}`;
    if (!values.includes(entry)) onChange([...values, entry]);
    setInput("");
  }

  return (
    <div>
      <p className={labelCls}>{label}</p>
      <div className="flex gap-2 mb-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
          placeholder={placeholder ?? "e.g. ultrasound machines"}
          className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <button type="button" onClick={addKeyword}
          className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs rounded-lg transition-colors">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((v) => {
            const display = v.replace(`${prefix}:search:`, "").replace(/\+/g, " ");
            return (
              <span key={v}
                className="flex items-center gap-1 px-2 py-1 bg-gray-800 border border-gray-700 rounded-full text-xs text-gray-300">
                {display}
                <button onClick={() => onChange(values.filter((x) => x !== v))}
                  className="text-gray-600 hover:text-red-400 transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {values.length === 0 && (
        <p className="text-xs text-gray-600 italic">No keywords yet — add above to set default products for new pages.</p>
      )}
    </div>
  );
}

// ─── Live product search panel ───────────────────────────────

function LiveSearch({
  network,
  promoted,
  onPromote,
  onUnpromote,
  promoteBusy,
}: {
  network: NetworkId;
  promoted: Record<string, boolean>;
  onPromote: (p: LiveProduct) => void;
  onUnpromote: (p: LiveProduct) => void;
  promoteBusy: Record<string, boolean>;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<LiveProduct[]>([]);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function triggerSearch(q: string) {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      setErr(null);
      try {
        const res = await affiliates.liveSearch(network, q);
        setResults(res.data);
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : "Search failed");
      } finally {
        setSearching(false);
      }
    }, 500);
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input value={query} onChange={(e) => { setQuery(e.target.value); triggerSearch(e.target.value); }}
          placeholder={`Search ${network} products…`}
          className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
        {searching && <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />}
      </div>

      {err && <p className="text-xs text-red-400 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{err}</p>}

      {results.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {results.map((p) => (
            <div key={p.externalId} className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden flex flex-col group">
              <div className="relative aspect-square bg-gray-800">
                {p.imageUrl
                  ? <img src={p.imageUrl} alt={p.title} className="w-full h-full object-contain p-2" loading="lazy" />
                  : <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-gray-600" /></div>}
                <button onClick={() => promoted[p.externalId] ? onUnpromote(p) : onPromote(p)}
                  disabled={promoteBusy[p.externalId]}
                  className={`absolute top-2 right-2 p-1.5 rounded-full transition-all disabled:opacity-50 ${
                    promoted[p.externalId]
                      ? "bg-brand-500 text-white"
                      : "bg-gray-900/80 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-brand-400"
                  }`}>
                  <Star className="w-3.5 h-3.5" fill={promoted[p.externalId] ? "currentColor" : "none"} />
                </button>
              </div>
              <div className="p-2.5 flex flex-col gap-1 flex-1">
                <p className="text-[11px] font-medium text-gray-100 line-clamp-2 leading-snug">{p.title}</p>
                {p.merchantName && <p className="text-[10px] text-gray-500">{p.merchantName}</p>}
                <div className="flex items-center justify-between mt-auto pt-1">
                  <span className="text-xs font-semibold text-green-400">
                    {p.price != null ? `${p.currency ?? "$"}${p.price.toFixed(2)}` : "—"}
                  </span>
                  <a href={p.productUrl} target="_blank" rel="noopener noreferrer"
                    className="p-1 text-gray-500 hover:text-gray-300">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!searching && query && results.length === 0 && !err && (
        <p className="text-xs text-gray-600 text-center py-4">No results for "{query}"</p>
      )}
    </div>
  );
}

// ─── Advertiser toggle list ───────────────────────────────────

function AdvertiserToggleList<T extends { id: string; name: string; primaryRegion?: string; logoUrl?: string }>({
  advertisers,
  selected,
  onToggle,
  loading,
  onLoad,
  loadLabel,
}: {
  advertisers: T[];
  selected: Set<string>;
  onToggle: (id: string, name: string) => void;
  loading: boolean;
  onLoad: () => void;
  loadLabel: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className={labelCls + " mb-0"}>Active Advertisers for this Site</p>
        <button onClick={onLoad} disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-lg border border-gray-700 transition-colors disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Loading…" : loadLabel}
        </button>
      </div>
      {advertisers.length === 0 ? (
        <p className="text-xs text-gray-600 italic">Click "{loadLabel}" to load your joined programmes.</p>
      ) : (
        <div className="border border-gray-700 rounded-lg divide-y divide-gray-800 max-h-64 overflow-y-auto">
          {advertisers.map((adv) => {
            const active = selected.has(adv.id);
            return (
              <label key={adv.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${active ? "bg-brand-500/5" : "hover:bg-gray-800/50"}`}>
                <input type="checkbox" checked={active} onChange={() => onToggle(adv.id, adv.name)}
                  className="w-3.5 h-3.5 rounded border-gray-600 bg-gray-800 accent-brand-500" />
                {adv.logoUrl
                  ? <img src={adv.logoUrl} alt={adv.name} className="w-7 h-7 object-contain rounded flex-shrink-0" />
                  : <div className="w-7 h-7 bg-gray-800 rounded flex items-center justify-center flex-shrink-0"><Building2 className="w-3.5 h-3.5 text-gray-600" /></div>}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-200 truncate">{adv.name}</p>
                  {adv.primaryRegion && <p className="text-[10px] text-gray-500">{adv.primaryRegion}</p>}
                </div>
                {active && <ChevronRight className="w-3.5 h-3.5 text-brand-400 flex-shrink-0" />}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Product feed row ─────────────────────────────────────────

function FeedRow({
  feed,
  onSync,
  onDelete,
  syncing,
}: {
  feed: SiteProductFeed;
  onSync: () => void;
  onDelete: () => void;
  syncing: boolean;
}) {
  const statusColor: Record<string, string> = {
    success: "text-green-400",
    error: "text-red-400",
    running: "text-blue-400",
    idle: "text-gray-500",
  };
  const st = feed.lastSyncStatus ?? "idle";
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-800/50 border border-gray-700 rounded-lg">
      <Rss className="w-4 h-4 text-gray-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-200 truncate">{feed.name}</p>
        <p className={`text-[10px] ${statusColor[st]}`}>
          {st === "running" ? "Syncing…" : st === "success" ? `Synced · ${feed.lastSyncSummary?.productsUpserted ?? 0} products` : st === "error" ? `Error · ${feed.lastSyncSummary?.errors?.[0] ?? ""}` : "Never synced"}
        </p>
      </div>
      <button onClick={onSync} disabled={syncing || st === "running"}
        className="p-1.5 text-gray-500 hover:text-blue-400 disabled:opacity-50 transition-colors">
        <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
      </button>
      <button onClick={onDelete} className="p-1.5 text-gray-500 hover:text-red-400 transition-colors">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Main SiteAffiliatesTab component ────────────────────────

export function SiteAffiliatesTab({
  site,
  onSaved,
}: {
  site: Site;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<NetworkId>("ebay");
  const [tenantConfig, setTenantConfig] = useState<AffiliateConfig | null>(null);
  const [overrides, setOverrides] = useState<SiteAffiliateConfig>(
    site.config?.affiliateOverrides ?? {}
  );
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Default product keywords (per-network, stored in affiliateOverrides)
  // ebay: overrides.ebay.searchTerms, amazon: overrides.amazon.searchTerms
  // stored as string[] of "ebay:search:..." / "amazon:search:..."
  const ebayTerms: string[] = (overrides as Record<string, Record<string, string[]>>).ebay?.searchTerms ?? [];
  const amazonTerms: string[] = (overrides as Record<string, Record<string, string[]>>).amazon?.searchTerms ?? [];

  // AWIN programmes
  const [awinPrograms, setAwinPrograms] = useState<AwinProgram[]>([]);
  const [awinLoading, setAwinLoading] = useState(false);

  // CJ advertisers
  const [cjAdvertisers, setCjAdvertisers] = useState<CjAdvertiser[]>([]);
  const [cjLoading, setCjLoading] = useState(false);

  // Product feeds (AWIN)
  const [feeds, setFeeds] = useState<SiteProductFeed[]>([]);
  const [feedsLoading, setFeedsLoading] = useState(false);
  const [syncingFeed, setSyncingFeed] = useState<string | null>(null);
  const [addFeedOpen, setAddFeedOpen] = useState(false);
  const [newFeedUrl, setNewFeedUrl] = useState("");
  const [newFeedName, setNewFeedName] = useState("");
  const [addingFeed, setAddingFeed] = useState(false);

  // Promoted product state (for live search panels)
  const [promoted, setPromoted] = useState<Record<string, boolean>>({});
  const [promoteBusy, setPromoteBusy] = useState<Record<string, boolean>>({});

  useEffect(() => {
    void loadTenantConfig();
  }, []);

  useEffect(() => {
    if (selected === "awin") void loadFeeds();
  }, [selected]);

  async function loadTenantConfig() {
    try {
      const res = await affiliates.get();
      setTenantConfig(res.data);
    } catch {
      // non-fatal
    }
  }

  async function loadFeeds() {
    setFeedsLoading(true);
    try {
      const res = await affiliates.listProductFeeds(site._id);
      setFeeds(res.data);
    } catch { /* non-fatal */ }
    finally { setFeedsLoading(false); }
  }

  async function handleSave() {
    setSaving(true);
    setErr(null);
    try {
      await sites.update(site._id, {
        config: { ...site.config, affiliateOverrides: overrides },
      });
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2500);
      onSaved();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function setEbay(patch: Partial<SiteAffiliateConfig["ebay"] & { searchTerms: string[] }>) {
    setOverrides((prev) => ({ ...prev, ebay: { ...prev.ebay, ...patch } }));
  }

  function setAmazon(patch: Partial<SiteAffiliateConfig["amazon"] & { searchTerms: string[] }>) {
    setOverrides((prev) => ({ ...prev, amazon: { ...prev.amazon, ...patch } } as SiteAffiliateConfig));
  }

  function setAwin(patch: Partial<SiteAwinConfig>) {
    setOverrides((prev) => ({ ...prev, awin: { ...prev.awin, ...patch } }));
  }

  function setCj(patch: Partial<SiteCjConfig>) {
    setOverrides((prev) => ({ ...prev, cj: { ...(prev.cj as SiteCjConfig | undefined), ...patch } }));
  }

  // AWIN advertiser selection
  const awinSelected = new Set<string>(
    (overrides.awin?.advertisers ?? []).map((a) => a.advertiserId)
  );

  function toggleAwinAdvertiser(id: string, name: string) {
    const cur = overrides.awin?.advertisers ?? [];
    const next = awinSelected.has(id)
      ? cur.filter((a) => a.advertiserId !== id)
      : [...cur, { advertiserId: id, name }];
    setAwin({ advertisers: next });
  }

  // CJ advertiser selection
  const cjSelected = new Set<string>(
    ((overrides.cj as SiteCjConfig | undefined)?.advertisers ?? []).map((a) => a.advertiserId)
  );

  function toggleCjAdvertiser(id: string, name: string) {
    const cur = (overrides.cj as SiteCjConfig | undefined)?.advertisers ?? [];
    const next = cjSelected.has(id)
      ? cur.filter((a) => a.advertiserId !== id)
      : [...cur, { advertiserId: id, name }];
    setCj({ advertisers: next });
  }

  async function loadAwinPrograms() {
    setAwinLoading(true);
    try {
      const res = await affiliates.listAwinPrograms();
      setAwinPrograms(res.data);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Failed to load AWIN programs");
    } finally {
      setAwinLoading(false);
    }
  }

  async function loadCjAdvertisers() {
    setCjLoading(true);
    try {
      const res = await affiliates.listCjAdvertisers();
      setCjAdvertisers(res.data);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Failed to load CJ advertisers");
    } finally {
      setCjLoading(false);
    }
  }

  async function syncFeed(feedId: string) {
    setSyncingFeed(feedId);
    try {
      await affiliates.syncProductFeed(site._id, feedId);
      await loadFeeds();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Sync failed");
    } finally {
      setSyncingFeed(null);
    }
  }

  async function deleteFeed(feedId: string) {
    try {
      await affiliates.deleteProductFeed(site._id, feedId);
      await loadFeeds();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Delete failed");
    }
  }

  async function addFeed(e: FormEvent) {
    e.preventDefault();
    setAddingFeed(true);
    try {
      await affiliates.createProductFeed(site._id, {
        name: newFeedName || "AWIN Feed",
        sourceUrl: newFeedUrl,
        provider: "awin",
        enabled: true,
      });
      setAddFeedOpen(false);
      setNewFeedUrl("");
      setNewFeedName("");
      await loadFeeds();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Failed to add feed");
    } finally {
      setAddingFeed(false);
    }
  }

  async function handlePromote(p: LiveProduct) {
    setPromoteBusy((b) => ({ ...b, [p.externalId]: true }));
    try {
      await affiliates.promote(p);
      setPromoted((prev) => ({ ...prev, [p.externalId]: true }));
    } catch { /* non-fatal */ }
    finally { setPromoteBusy((b) => ({ ...b, [p.externalId]: false })); }
  }

  async function handleUnpromote(p: LiveProduct) {
    setPromoteBusy((b) => ({ ...b, [p.externalId]: true }));
    try {
      await affiliates.unpromote(p.externalId, p.network);
      setPromoted((prev) => ({ ...prev, [p.externalId]: false }));
    } catch { /* non-fatal */ }
    finally { setPromoteBusy((b) => ({ ...b, [p.externalId]: false })); }
  }

  const ebayConnected = !!tenantConfig?.ebay;
  const amazonConnected = !!tenantConfig?.amazon;
  const awinConnected = !!tenantConfig?.awin;
  const cjConnected = !!tenantConfig?.cj;

  return (
    <div className="flex gap-0 h-full min-h-[600px]">
      {/* ─── Left nav ─────────────────────────────────────────── */}
      <div className="w-44 flex-shrink-0 border-r border-gray-800 pr-0 space-y-0.5 pt-1">
        {NETWORKS.map(({ id, label, abbr, color }) => {
          const connected =
            id === "ebay" ? ebayConnected
            : id === "amazon" ? amazonConnected
            : id === "awin" ? awinConnected
            : cjConnected;
          return (
            <button
              key={id}
              onClick={() => setSelected(id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors ${
                selected === id
                  ? "bg-brand-500/10 text-brand-300"
                  : "text-gray-400 hover:bg-gray-800/60 hover:text-gray-200"
              }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                selected === id ? "bg-brand-500/15" : `bg-${color}-500/10`
              }`}>
                <span className={`text-[11px] font-bold ${selected === id ? "text-brand-400" : `text-${color}-400`}`}>
                  {abbr}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium leading-tight truncate">{label}</p>
                <p className={`text-[10px] mt-0.5 ${connected ? "text-green-400" : "text-gray-600"}`}>
                  {connected ? "Connected" : "Not set up"}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* ─── Right panel ──────────────────────────────────────── */}
      <div className="flex-1 pl-5 space-y-6 overflow-y-auto">
        {/* Global save bar */}
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-100">
            {NETWORKS.find((n) => n.id === selected)?.label}
          </h2>
          <div className="flex items-center gap-2">
            {savedMsg && <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Saved</span>}
            {err && <span className="text-xs text-red-400">{err}</span>}
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors">
              <Save className="w-3.5 h-3.5" />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {/* ═══ EPN panel ═══════════════════════════════════════ */}
        {selected === "ebay" && (
          <div className="space-y-6">
            {!ebayConnected && <NotConnectedBanner network="eBay Partner Network" />}

            <div>
              <SectionHeader title="Site Overrides" subtitle="Override account defaults for this site." />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Campaign ID</label>
                  <input value={overrides.ebay?.campaignId ?? ""}
                    onChange={(e) => setEbay({ campaignId: e.target.value })}
                    placeholder="5338904xxx"
                    className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Publisher ID <span className="text-gray-600">(optional)</span></label>
                  <input value={overrides.ebay?.publisherId ?? ""}
                    onChange={(e) => setEbay({ publisherId: e.target.value })}
                    placeholder="Inherits from account"
                    className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Marketplace Region</label>
                  <select value={overrides.ebay?.region ?? ""}
                    onChange={(e) => setEbay({ region: e.target.value || undefined })}
                    className={selectCls}>
                    <option value="">Default (US)</option>
                    {["US", "CA", "UK", "DE", "FR", "IT", "ES", "AU"].map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <SectionHeader title="Default Product Keywords"
                subtitle="Search terms used when generating new pages. Products are inserted as ebay:search:… links." />
              <KeywordChips
                label="Search Keywords"
                prefix="ebay"
                values={ebayTerms}
                onChange={(next) => setEbay({ searchTerms: next } as Record<string, string[]>)}
                placeholder="e.g. ultrasound machines"
              />
            </div>

            {ebayConnected && (
              <div>
                <SectionHeader title="Live Product Search"
                  subtitle="Browse eBay listings and star products to promote across this site." />
                <LiveSearch network="ebay" promoted={promoted} promoteBusy={promoteBusy}
                  onPromote={handlePromote} onUnpromote={handleUnpromote} />
              </div>
            )}

            <InfoBox text="EPN compliance: include affiliate disclosure near all eBay links. Prices update in real-time from eBay — do not hard-code prices." />
          </div>
        )}

        {/* ═══ Amazon panel ════════════════════════════════════ */}
        {selected === "amazon" && (
          <div className="space-y-6">
            {!amazonConnected && <NotConnectedBanner network="Amazon Associates" />}

            <div>
              <SectionHeader title="Site Overrides" subtitle="Override account defaults for this site." />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Tracking ID</label>
                  <input value={overrides.amazon?.trackingId ?? ""}
                    onChange={(e) => setAmazon({ trackingId: e.target.value })}
                    placeholder="yoursite-20"
                    className={inputCls} />
                  <p className="text-[10px] text-gray-600 mt-1">Per-site Associates tag</p>
                </div>
                <div>
                  <label className={labelCls}>Marketplace Region</label>
                  <select value={overrides.amazon?.region ?? ""}
                    onChange={(e) => setAmazon({ region: e.target.value || undefined })}
                    className={selectCls}>
                    <option value="">Inherits from account</option>
                    {AMAZON_REGIONS.map((r) => (
                      <option key={r} value={r}>{AMAZON_REGION_LABELS[r]} ({r})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <SectionHeader title="Default Product Keywords"
                subtitle="Search terms added to new pages as amazon:search:… product links. Keeps pricing live — never hard-codes ASINs." />
              <KeywordChips
                label="Search Keywords"
                prefix="amazon"
                values={amazonTerms}
                onChange={(next) => setAmazon({ searchTerms: next } as Record<string, string[]>)}
                placeholder="e.g. portable ultrasound machine"
              />
            </div>

            <InfoBox text="Amazon Associates policy: do not quote specific prices. All links must include your tracking ID. Product images must link back to Amazon and cannot be hosted locally." />
          </div>
        )}

        {/* ═══ AWIN panel ══════════════════════════════════════ */}
        {selected === "awin" && (
          <div className="space-y-6">
            {!awinConnected && <NotConnectedBanner network="AWIN" />}

            <div>
              <SectionHeader title="Site Overrides" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Publisher ID Override <span className="text-gray-600">(optional)</span></label>
                  <input value={overrides.awin?.publisherId ?? ""}
                    onChange={(e) => setAwin({ publisherId: e.target.value })}
                    placeholder="Uses account default"
                    className={inputCls} />
                </div>
              </div>
            </div>

            {awinConnected && (
              <div>
                <SectionHeader title="Active Advertisers"
                  subtitle="Select which AWIN programmes are active for this site. Only selected advertisers' links will be inserted." />
                <AdvertiserToggleList
                  advertisers={awinPrograms}
                  selected={awinSelected}
                  onToggle={toggleAwinAdvertiser}
                  loading={awinLoading}
                  onLoad={loadAwinPrograms}
                  loadLabel="Load Programmes"
                />
              </div>
            )}

            {awinConnected && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-100">Product Feeds</p>
                    <p className="text-xs text-gray-500 mt-0.5">AWIN feed URLs to sync product catalogs for this site.</p>
                  </div>
                  <button onClick={() => setAddFeedOpen((v) => !v)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 text-xs rounded-lg transition-colors">
                    <Plus className="w-3.5 h-3.5" />
                    Add Feed
                  </button>
                </div>

                {addFeedOpen && (
                  <form onSubmit={addFeed} className="mb-3 p-3 bg-gray-800/50 border border-gray-700 rounded-lg space-y-2">
                    <div>
                      <label className={labelCls}>Feed Name</label>
                      <input value={newFeedName} onChange={(e) => setNewFeedName(e.target.value)}
                        placeholder="e.g. Medical Equipment Feed"
                        className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>AWIN Feed List URL *</label>
                      <input value={newFeedUrl} onChange={(e) => setNewFeedUrl(e.target.value)}
                        required placeholder="https://datafeed.awin.com/cgi-bin/…"
                        className={inputCls} />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button type="submit" disabled={addingFeed || !newFeedUrl}
                        className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-xs rounded-lg transition-colors">
                        {addingFeed ? "Adding…" : "Add"}
                      </button>
                      <button type="button" onClick={() => setAddFeedOpen(false)}
                        className="px-3 py-1.5 text-gray-400 hover:text-gray-200 text-xs rounded-lg transition-colors">Cancel</button>
                    </div>
                  </form>
                )}

                {feedsLoading ? (
                  <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
                ) : feeds.length === 0 ? (
                  <p className="text-xs text-gray-600 italic">No feeds yet. Add a feed URL to start syncing products.</p>
                ) : (
                  <div className="space-y-2">
                    {feeds.map((f) => (
                      <FeedRow key={f.id} feed={f}
                        onSync={() => syncFeed(f.id)}
                        onDelete={() => deleteFeed(f.id)}
                        syncing={syncingFeed === f.id} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {awinConnected && (
              <div>
                <SectionHeader title="Browse Synced Products"
                  subtitle="Search products synced from your AWIN feeds above." />
                <LiveSearch network="awin" promoted={promoted} promoteBusy={promoteBusy}
                  onPromote={handlePromote} onUnpromote={handleUnpromote} />
              </div>
            )}

            <InfoBox text="AWIN compliance: respect each advertiser's programme terms. Include affiliate disclosure on all pages. Refresh product data at least weekly." />
          </div>
        )}

        {/* ═══ CJ panel ════════════════════════════════════════ */}
        {selected === "cj" && (
          <div className="space-y-6">
            {!cjConnected && <NotConnectedBanner network="Commission Junction" />}

            <div>
              <SectionHeader title="Site Overrides" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Website ID</label>
                  <input value={(overrides.cj as SiteCjConfig | undefined)?.websiteId ?? ""}
                    onChange={(e) => setCj({ websiteId: e.target.value })}
                    placeholder="1234567"
                    className={inputCls} />
                  <p className="text-[10px] text-gray-600 mt-1">Your CJ website property ID for this domain</p>
                </div>
              </div>
            </div>

            {cjConnected && (
              <div>
                <SectionHeader title="Active Advertisers"
                  subtitle="Select which CJ advertisers are active for this site." />
                <AdvertiserToggleList
                  advertisers={cjAdvertisers}
                  selected={cjSelected}
                  onToggle={toggleCjAdvertiser}
                  loading={cjLoading}
                  onLoad={loadCjAdvertisers}
                  loadLabel="Load Advertisers"
                />
              </div>
            )}

            {cjConnected && (
              <div>
                <SectionHeader title="Browse Products"
                  subtitle="Live product search from CJ's catalog. Star products to promote on this site." />
                <LiveSearch network="cj" promoted={promoted} promoteBusy={promoteBusy}
                  onPromote={handlePromote} onUnpromote={handleUnpromote} />
              </div>
            )}

            <InfoBox text="CJ compliance: comply with each advertiser's programme terms. Include FTC-required affiliate disclosure. Do not modify deep-link tracking parameters." />
          </div>
        )}
      </div>
    </div>
  );
}
